import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MockupRenderer } from "../mockup-engine";
import { prepareArtwork, renderTemplate, normalizeAppearance } from "../mockup-engine/cpuRenderer";
import { designToTemplate, templateDeltaToDesign, templateToDesign } from "./surface";
import { keepInArea, normalizeAngle, pointAt, scaleAboutCenter } from "./placement";

/**
 * Live mockup preview with direct manipulation.
 *
 * Drag on the product to move the design, drag a corner to resize, drag the
 * round handle to rotate (Shift snaps to 15°). Arrow keys nudge, +/- resize,
 * [ and ] rotate.
 */
export default function MockupCanvas({
  template,
  artwork,
  placement,
  natural,
  appearance,
  grid = false,
  interactive = true,
  onChange,
  onCommit,
  onRendererError,
  children,
}) {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const rendererRef = useRef(null);
  const fallbackRef = useRef(false);
  const frameRef = useRef(0);
  const dragRef = useRef(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [, forceRender] = useState(0);

  const latest = useRef({});
  latest.current = { template, artwork, placement, appearance, grid };

  // --- Renderer lifecycle ---------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    try {
      rendererRef.current = new MockupRenderer(canvas, {
        onContextRestored: () => scheduleDraw(),
      });
    } catch (error) {
      console.warn("WebGL2 unavailable, using the CPU preview", error);
      fallbackRef.current = true;
      onRendererError?.(error);
      forceRender((n) => n + 1);
    }
    return () => {
      cancelAnimationFrame(frameRef.current);
      rendererRef.current?.dispose();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const r = rendererRef.current;
    if (r && template) {
      try {
        r.setTemplate(template);
      } catch (error) {
        console.warn("Template upload failed, using the CPU preview", error);
        fallbackRef.current = true;
        onRendererError?.(error);
      }
    }
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  useEffect(() => {
    rendererRef.current?.setArtwork(artwork || null);
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artwork]);

  useEffect(() => {
    scheduleDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, appearance, grid]);

  const scheduleDraw = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      const { template: t, artwork: art, placement: p, appearance: a, grid: g } = latest.current;
      if (!t) return;
      if (fallbackRef.current) drawCpu(canvasRef.current, t, art, p, a);
      else rendererRef.current?.draw({ placement: art ? p : null, appearance: a, grid: g });
    });
  }, []);

  // --- Layout: fit the template into the stage -------------------------------
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fitted = useMemo(() => {
    if (!template || !box.width || !box.height) return null;
    const s = Math.min(box.width / template.width, box.height / template.height);
    return { width: Math.floor(template.width * s), height: Math.floor(template.height * s), k: template.width / Math.max(1, template.width * s) };
  }, [template, box]);

  // --- Overlay geometry (template pixels) -------------------------------------
  // A plain rectangle, turned with the design, that encloses the warped
  // design. Following the warp's curve looked odd on some PSDs (the bend
  // depends on how the template was built), so the frame stays straight.
  const overlay = useMemo(() => {
    if (!template || !placement || !artwork || !fitted) return null;
    const toT = (pt) => designToTemplate(template, pt);
    const samples = 24, pts = [];
    const edge = (fx0, fy0, fx1, fy1) => {
      for (let i = 0; i < samples; i++) {
        const t = i / samples;
        pts.push(toT(pointAt(placement, fx0 + (fx1 - fx0) * t, fy0 + (fy1 - fy0) * t)));
      }
    };
    edge(0, 0, 1, 0);
    edge(1, 0, 1, 1);
    edge(1, 1, 0, 1);
    edge(0, 1, 0, 0);
    const center = toT(pointAt(placement, 0.5, 0.5));

    // Frame axes: the design's horizontal direction at its centre on screen,
    // and its perpendicular pointing towards the design's bottom.
    const left = toT(pointAt(placement, 0.45, 0.5)), right = toT(pointAt(placement, 0.55, 0.5));
    let ux = right.x - left.x, uy = right.y - left.y;
    let len = Math.hypot(ux, uy);
    if (len < 1e-6) {
      const r = (placement.rotation * Math.PI) / 180;
      ux = Math.cos(r);
      uy = Math.sin(r);
      len = 1;
    }
    ux /= len;
    uy /= len;
    let vx = -uy, vy = ux;
    const below = toT(pointAt(placement, 0.5, 0.55));
    if ((below.x - center.x) * vx + (below.y - center.y) * vy < 0) {
      vx = -vx;
      vy = -vy;
    }

    let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
    for (const p of pts) {
      const u = p.x * ux + p.y * uy, v = p.x * vx + p.y * vy;
      if (u < u0) u0 = u;
      if (u > u1) u1 = u;
      if (v < v0) v0 = v;
      if (v > v1) v1 = v;
    }
    const at = (u, v) => ({ x: u * ux + v * vx, y: u * uy + v * vy });
    // Top-left, top-right, bottom-right, bottom-left (as the design's corners).
    const cornerPts = [at(u0, v0), at(u1, v0), at(u1, v1), at(u0, v1)];
    const path = `M${cornerPts.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join("L")}Z`;
    const top = at((u0 + u1) / 2, v0);
    const k = fitted.k;
    const rotateHandle = { x: top.x - vx * 30 * k, y: top.y - vy * 30 * k };
    return { path, corners: cornerPts, center, top, rotateHandle, k };
  }, [template, placement, artwork, fitted]);

  // --- Pointer interaction ----------------------------------------------------
  const toTemplatePoint = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    return { x: ((event.clientX - rect.left) * template.width) / rect.width, y: ((event.clientY - rect.top) * template.height) / rect.height };
  };

  const limit = (p) => keepInArea(p, template.designWidth, template.designHeight);
  const emit = (next) => onChange?.(next);

  const onPointerDown = (event) => {
    if (!interactive || !template || !placement || !artwork || event.button > 0) return;
    const handle = event.target?.dataset?.handle;
    const pt = toTemplatePoint(event);
    const designPt = templateToDesign(template, pt.x, pt.y);
    const insideOutline = overlay && pointInPath(svgRef.current, overlay.path, pt);
    if (!handle && !designPt && !insideOutline) return;
    event.preventDefault();
    svgRef.current.setPointerCapture(event.pointerId);
    const center = overlay.center;
    dragRef.current = {
      mode: handle || "move",
      pointerId: event.pointerId,
      start: placement,
      current: placement,
      last: pt,
      lastDesign: designPt,
      center,
      startDist: Math.max(1, Math.hypot(pt.x - center.x, pt.y - center.y)),
      startAngle: Math.atan2(pt.y - center.y, pt.x - center.x),
    };
  };

  const onPointerMove = (event) => {
    const d = dragRef.current;
    if (!d || event.pointerId !== d.pointerId) return;
    const pt = toTemplatePoint(event);
    let next = d.current;
    if (d.mode === "move") {
      const designPt = templateToDesign(template, pt.x, pt.y);
      let delta;
      if (designPt && d.lastDesign) delta = { x: designPt.x - d.lastDesign.x, y: designPt.y - d.lastDesign.y };
      else delta = templateDeltaToDesign(template, { x: next.cx, y: next.cy }, pt.x - d.last.x, pt.y - d.last.y);
      next = limit({ ...next, cx: next.cx + delta.x, cy: next.cy + delta.y });
      d.lastDesign = designPt;
      d.last = pt;
    } else if (d.mode === "scale") {
      const ratio = Math.hypot(pt.x - d.center.x, pt.y - d.center.y) / d.startDist;
      const scaled = scaleAboutCenter(d.start, ratio, natural || d.start);
      next = limit({ ...next, width: scaled.width, height: scaled.height });
    } else if (d.mode === "rotate") {
      let delta = Math.atan2(pt.y - d.center.y, pt.x - d.center.x) - d.startAngle;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      let angle = d.start.rotation + (delta * 180) / Math.PI;
      if (event.shiftKey) angle = Math.round(angle / 15) * 15;
      next = limit({ ...next, rotation: normalizeAngle(angle) });
    }
    d.current = next;
    emit(next);
  };

  const endDrag = (event) => {
    const d = dragRef.current;
    if (!d || (event && event.pointerId !== d.pointerId)) return;
    dragRef.current = null;
    if (svgRef.current?.hasPointerCapture?.(d.pointerId)) svgRef.current.releasePointerCapture(d.pointerId);
    if (d.current !== d.start) onCommit?.(d.current, d.mode);
  };

  const onKeyDown = (event) => {
    if (!interactive || !placement || !artwork || !template) return;
    const step = (event.shiftKey ? 0.1 : 0.01) * template.designWidth;
    let next = null;
    switch (event.key) {
      case "ArrowLeft": next = { ...placement, cx: placement.cx - step }; break;
      case "ArrowRight": next = { ...placement, cx: placement.cx + step }; break;
      case "ArrowUp": next = { ...placement, cy: placement.cy - step }; break;
      case "ArrowDown": next = { ...placement, cy: placement.cy + step }; break;
      case "+": case "=": next = { ...placement, ...scaleAboutCenter(placement, 1.05, natural || placement) }; break;
      case "-": case "_": next = { ...placement, ...scaleAboutCenter(placement, 1 / 1.05, natural || placement) }; break;
      case "[": next = { ...placement, rotation: normalizeAngle(placement.rotation - (event.shiftKey ? 15 : 1)) }; break;
      case "]": next = { ...placement, rotation: normalizeAngle(placement.rotation + (event.shiftKey ? 15 : 1)) }; break;
      default: return;
    }
    event.preventDefault();
    next = limit(next);
    emit(next);
    onCommit?.(next, "keyboard");
  };

  const showOverlay = interactive && overlay;
  const k = overlay?.k || 1;

  return (
    <div className="ms-stage" ref={stageRef}>
      <div
        className="ms-canvas-wrap"
        style={fitted ? { width: fitted.width, height: fitted.height } : { visibility: "hidden" }}
        tabIndex={interactive && artwork ? 0 : -1}
        onKeyDown={onKeyDown}
        aria-label="Mockup preview. Drag to move the design, use the corner handles to resize and the round handle to rotate."
      >
        <canvas ref={canvasRef} className="ms-canvas" />
        {template && (
          <svg
            ref={svgRef}
            className={`ms-overlay${artwork && interactive ? " is-interactive" : ""}`}
            viewBox={`0 0 ${template.width} ${template.height}`}
            preserveAspectRatio="none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onLostPointerCapture={() => dragRef.current && endDrag()}
          >
            {showOverlay && (
              <g>
                <path d={overlay.path} className="ms-outline" style={{ strokeWidth: 1.5 * k }} />
                <line x1={overlay.top.x} y1={overlay.top.y} x2={overlay.rotateHandle.x} y2={overlay.rotateHandle.y} className="ms-outline" style={{ strokeWidth: 1.5 * k }} />
                {overlay.corners.map((c, i) => (
                  <rect
                    key={i}
                    data-handle="scale"
                    className={`ms-handle ms-handle-${i % 2 ? "nesw" : "nwse"}`}
                    x={c.x - 7 * k}
                    y={c.y - 7 * k}
                    width={14 * k}
                    height={14 * k}
                    rx={3 * k}
                    style={{ strokeWidth: 2 * k }}
                  />
                ))}
                <circle data-handle="rotate" className="ms-handle ms-handle-rotate" cx={overlay.rotateHandle.x} cy={overlay.rotateHandle.y} r={10 * k} style={{ strokeWidth: 2 * k }} />
              </g>
            )}
          </svg>
        )}
        {children}
      </div>
    </div>
  );
}

function pointInPath(svg, d, pt) {
  if (!svg || typeof DOMMatrix === "undefined") return false;
  try {
    const path = new Path2D(d);
    const ctx = pointInPath.ctx || (pointInPath.ctx = document.createElement("canvas").getContext("2d"));
    return ctx.isPointInPath(path, pt.x, pt.y);
  } catch {
    return false;
  }
}

// CPU fallback for browsers without WebGL2 (slower; also used if the GPU fails).
function drawCpu(canvas, template, artwork, placement, appearance) {
  if (!canvas) return;
  canvas.width = template.width;
  canvas.height = template.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const settings = normalizeAppearance(appearance);
  let art = { width: 1, height: 1, data: new Uint8ClampedArray(4) };
  if (artwork && placement) {
    const scale = Math.min(1, 1024 / Math.max(template.designWidth, template.designHeight));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(template.designWidth * scale));
    c.height = Math.max(1, Math.round(template.designHeight * scale));
    const cx = c.getContext("2d");
    cx.scale(scale, scale);
    cx.translate(placement.cx, placement.cy);
    cx.rotate((placement.rotation * Math.PI) / 180);
    cx.drawImage(artwork, -placement.width / 2, -placement.height / 2, placement.width, placement.height);
    art = cx.getImageData(0, 0, c.width, c.height);
  }
  const pixels = renderTemplate(template, prepareArtwork(art, template, settings), settings);
  ctx.putImageData(new ImageData(pixels, template.width, template.height), 0, 0);
}
